import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogContent,
   DialogActions,
   Typography,
   Button,
   Box,
   Grid,
   Chip,
   IconButton,
   CircularProgress,
   Alert,
   Snackbar,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import {
   Close as CloseIcon,
   LocationOn as LocationIcon,
   CalendarToday as CalendarIcon,
   Event as EventIcon,
   Flight as FlightIcon,
   PhotoLibrary as AlbumIcon,
   AccessTime as TimeIcon,
   Person as PersonIcon,
   Sync as SyncIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';

export interface TripEventModalData {
   id: string;
   type: 'trip' | 'event' | 'album';
   title: string;
   description?: string;
   startDate?: Date;
   endDate?: Date;
   location?: string;
   coverImageUrl?: string;
   destination?: string;
   createdBy?: string;
   createdByName?: string;
   participantCount?: number;
   photoCount?: number;
   organizationId?: string; // For organization-specific navigation
}

interface TripEventModalProps {
   open: boolean;
   onClose: () => void;
   data: TripEventModalData | null;
}

const TripEventModal: React.FC<TripEventModalProps> = ({ open, onClose, data }) => {
   const navigate = useNavigate();
   const { currentUser } = useUser();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const [syncLoading, setSyncLoading] = useState(false);
   const [syncSuccess, setSyncSuccess] = useState(false);
   const [syncError, setSyncError] = useState<string | null>(null);
   const [userAuthenticated, setUserAuthenticated] = useState(false);
   const [pendingSync, setPendingSync] = useState(false);

   // Clean up any URL parameters from previous OAuth flows
   useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('google_auth')) {
         const newUrl = window.location.pathname;
         window.history.replaceState({}, document.title, newUrl);
      }
   }, []);

   if (!data) return null;

   // Debug logging
   console.log('TripEventModal: Rendering with data', data);

   const handleViewDetails = () => {
      let detailsPath = '';

      console.log('TripEventModal: Navigating to details', {
         data,
         organizationId: data.organizationId,
      });

      if (data.organizationId) {
         // Organization context navigation
         if (data.type === 'trip') {
            detailsPath = `/organizations/${data.organizationId}/trips/${data.id}`;
         } else if (data.type === 'event') {
            detailsPath = `/organizations/${data.organizationId}/events/${data.id}`;
         } else if (data.type === 'album') {
            detailsPath = `/organizations/${data.organizationId}/albums/${data.id}`;
         }
      } else {
         // Personal account navigation
         if (data.type === 'trip') {
            detailsPath = `/trip/${data.id}`;
         } else if (data.type === 'event') {
            detailsPath = `/events/${data.id}`;
         } else if (data.type === 'album') {
            detailsPath = `/albums/${data.id}`;
         }
      }

      console.log('TripEventModal: Navigation path', detailsPath);

      if (detailsPath) {
         navigate(detailsPath);
         onClose();
      } else {
         console.error('TripEventModal: No valid navigation path found');
      }
   };

   const handleSyncToGoogleCalendar = async () => {
      if (!data.startDate || data.type === 'album') return;

      setSyncLoading(true);
      setSyncError(null);
      setPendingSync(false); // Reset pending sync state

      try {
         if (!currentUser?.uid) {
            setSyncError('Please log in to sync with Google Calendar.');
            setSyncLoading(false);
            return;
         }

         // Check if user is authenticated with Google
         const authStatusResponse = await fetch(
            `/api/google-calendar/auth-status?userId=${currentUser.uid}`
         );
         const authStatus = await authStatusResponse.json();

         if (!authStatus.authenticated) {
            // Prepare event data to pass to OAuth flow
            const isAllDay = data.type === 'trip';
            const startDate = new Date(data.startDate);
            const endDate = data.endDate
               ? new Date(data.endDate)
               : isAllDay
                 ? new Date(startDate.getTime() + 24 * 60 * 60 * 1000)
                 : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

            const eventData = {
               summary: data.title,
               description:
                  data.description || `${data.type === 'trip' ? 'Trip' : 'Event'}: ${data.title}`,
               location: data.location || data.destination || '',
               startDateTime: isAllDay
                  ? startDate.toISOString().split('T')[0]
                  : startDate.toISOString(),
               endDateTime: isAllDay ? endDate.toISOString().split('T')[0] : endDate.toISOString(),
               timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
               allDay: isAllDay,
            };

            // User needs to authenticate with Google - pass event data
            const eventDataParam = encodeURIComponent(JSON.stringify(eventData));
            const authUrlResponse = await fetch(
               `/api/google-calendar/auth-url?userId=${currentUser.uid}&eventData=${eventDataParam}`
            );
            const { authUrl } = await authUrlResponse.json();

            if (authUrl) {
               // Redirect to Google OAuth (will come back to calendar page with success/error)
               window.location.href = authUrl;
               return;
            } else {
               setSyncError('Unable to generate Google authentication URL. Please try again.');
               setSyncLoading(false);
               return;
            }
         }

         // Prepare event data for Google Calendar
         const isAllDay = data.type === 'trip'; // Trips are typically all-day events
         const startDate = new Date(data.startDate);
         const endDate = data.endDate
            ? new Date(data.endDate)
            : isAllDay
              ? new Date(startDate.getTime() + 24 * 60 * 60 * 1000) // Next day for all-day trips
              : new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // 2 hours for timed events

         const eventData = {
            summary: data.title,
            description:
               data.description || `${data.type === 'trip' ? 'Trip' : 'Event'}: ${data.title}`,
            location: data.location || data.destination || '',
            startDateTime: isAllDay
               ? startDate.toISOString().split('T')[0] // YYYY-MM-DD format for all-day events
               : startDate.toISOString(), // Full ISO string for timed events
            endDateTime: isAllDay
               ? endDate.toISOString().split('T')[0] // YYYY-MM-DD format for all-day events
               : endDate.toISOString(), // Full ISO string for timed events
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            allDay: isAllDay,
            userId: currentUser.uid, // Pass user ID to identify whose calendar to use
         };

         console.log('Sending event data to Google Calendar:', eventData);

         const response = await fetch('/api/google-calendar/add-event', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify(eventData),
         });

         const result = await response.json();
         console.log('Google Calendar API response:', result);

         if (response.ok && result.success) {
            setSyncSuccess(true);
            setTimeout(() => setSyncSuccess(false), 3000); // Hide success message after 3 seconds
         } else {
            const errorMessage =
               result.error || result.message || 'Failed to sync to Google Calendar';
            throw new Error(errorMessage);
         }
      } catch (error) {
         console.error('Error syncing to Google Calendar:', error);
         setSyncError(error instanceof Error ? error.message : 'Failed to sync to Google Calendar');
      } finally {
         setSyncLoading(false);
      }
   };

   const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
         weekday: 'short',
         year: 'numeric',
         month: 'short',
         day: 'numeric',
      });
   };

   const formatDateRange = (startDate: Date, endDate?: Date) => {
      if (!endDate || startDate.toDateString() === endDate.toDateString()) {
         return formatDate(startDate);
      }
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
   };

   const getTypeIcon = () => {
      switch (data.type) {
         case 'trip':
            return <FlightIcon sx={{ color: 'primary.main' }} />;
         case 'event':
            return <EventIcon sx={{ color: 'secondary.main' }} />;
         case 'album':
            return <AlbumIcon sx={{ color: 'success.main' }} />;
         default:
            return <CalendarIcon />;
      }
   };

   const getTypeColor = () => {
      switch (data.type) {
         case 'trip':
            return 'primary';
         case 'event':
            return 'secondary';
         case 'album':
            return 'success';
         default:
            return 'primary';
      }
   };

   // Function to sanitize HTML for safe rendering
   const sanitizeHtml = (htmlString: string) => {
      // Basic sanitization - remove script tags and other potentially dangerous elements
      return htmlString
         .replace(/<script[^>]*>.*?<\/script>/gi, '')
         .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
         .replace(/javascript:/gi, '')
         .replace(/on\w+\s*=/gi, '');
   };

   return (
      <Dialog
         open={open}
         onClose={onClose}
         maxWidth={isMobile ? false : 'md'}
         fullWidth
         fullScreen={isMobile}
         PaperProps={{
            sx: {
               borderRadius: isMobile ? 0 : 2,
               overflow: 'hidden',
               margin: isMobile ? 0 : 2,
            },
         }}
      >
         {/* Header with cover image */}
         <Box sx={{ position: 'relative' }}>
            <Box
               sx={{
                  height: isMobile ? 250 : 200,
                  bgcolor:
                     data.type === 'trip'
                        ? 'primary.light'
                        : data.type === 'event'
                          ? 'secondary.light'
                          : 'success.light',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
               }}
            >
               {data.coverImageUrl ? (
                  <Box
                     component='img'
                     src={data.coverImageUrl}
                     alt={data.title}
                     sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                     }}
                  />
               ) : (
                  <Box
                     sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        background:
                           data.type === 'trip'
                              ? 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)'
                              : data.type === 'event'
                                ? 'linear-gradient(135deg, #f8bbd9 0%, #f48fb1 100%)'
                                : 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
                     }}
                  />
               )}

               {/* Overlay with gradient */}
               <Box
                  sx={{
                     position: 'absolute',
                     top: 0,
                     left: 0,
                     right: 0,
                     bottom: 0,
                     background:
                        'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)',
                  }}
               />

               {/* Close button */}
               <IconButton
                  onClick={onClose}
                  sx={{
                     position: 'absolute',
                     top: 8,
                     right: 8,
                     color: 'white',
                     bgcolor: 'rgba(0,0,0,0.5)',
                     '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.7)',
                     },
                  }}
               >
                  <CloseIcon />
               </IconButton>

               {/* Title and type overlay */}
               <Box
                  sx={{
                     position: 'absolute',
                     bottom: 16,
                     left: 16,
                     right: 16,
                     color: 'white',
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                     {getTypeIcon()}
                     <Typography variant='h5' sx={{ ml: 1, fontWeight: 'bold' }}>
                        {data.title}
                     </Typography>
                  </Box>
                  <Chip
                     label={data.type.charAt(0).toUpperCase() + data.type.slice(1)}
                     size='small'
                     sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 'bold',
                     }}
                  />
               </Box>
            </Box>
         </Box>

         <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
            <Grid container spacing={isMobile ? 1.5 : 2}>
               {/* Description */}
               {data.description && (
                  <Grid item xs={12}>
                     <Typography
                        variant='body1'
                        color='text.secondary'
                        paragraph
                        component='div'
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.description) }}
                        sx={{
                           '& p': { margin: '0 0 8px 0' },
                           '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
                           '& li': { marginBottom: '4px' },
                           '& br': { display: 'block', content: '""', marginTop: '8px' },
                        }}
                     />
                  </Grid>
               )}

               {/* Date information */}
               {data.startDate && (
                  <Grid item xs={12} sm={6}>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant='subtitle2' color='text.secondary'>
                           {data.type === 'album' ? 'Created' : 'Dates'}
                        </Typography>
                     </Box>
                     <Typography variant='body2'>
                        {data.endDate
                           ? formatDateRange(data.startDate, data.endDate)
                           : formatDate(data.startDate)}
                     </Typography>
                  </Grid>
               )}

               {/* Location information */}
               {(data.location || data.destination) && (
                  <Grid item xs={12} sm={6}>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant='subtitle2' color='text.secondary'>
                           {data.type === 'trip' ? 'Destination' : 'Location'}
                        </Typography>
                     </Box>
                     <Typography variant='body2'>{data.location || data.destination}</Typography>
                  </Grid>
               )}

               {/* Creator information */}
               {data.createdByName && (
                  <Grid item xs={12} sm={6}>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant='subtitle2' color='text.secondary'>
                           Created by
                        </Typography>
                     </Box>
                     <Typography variant='body2'>{data.createdByName}</Typography>
                  </Grid>
               )}

               {/* Participant count */}
               {data.participantCount !== undefined && data.participantCount > 0 && (
                  <Grid item xs={12} sm={6}>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant='subtitle2' color='text.secondary'>
                           Participants
                        </Typography>
                     </Box>
                     <Typography variant='body2'>
                        {data.participantCount} {data.participantCount === 1 ? 'person' : 'people'}
                     </Typography>
                  </Grid>
               )}

               {/* Photo count */}
               {data.photoCount !== undefined && data.photoCount > 0 && (
                  <Grid item xs={12} sm={6}>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AlbumIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant='subtitle2' color='text.secondary'>
                           Photos
                        </Typography>
                     </Box>
                     <Typography variant='body2'>
                        {data.photoCount} {data.photoCount === 1 ? 'photo' : 'photos'}
                     </Typography>
                  </Grid>
               )}
            </Grid>
         </DialogContent>

         <DialogActions
            sx={{
               px: isMobile ? 2 : 3,
               py: isMobile ? 2 : 2,
               gap: isMobile ? 1 : 1,
               flexDirection: isMobile ? 'column' : 'row',
               alignItems: isMobile ? 'stretch' : 'center',
            }}
         >
            {/* Google Calendar Sync Button - only show for trips and events with dates */}
            {data.startDate && data.type !== 'album' && (
               <Button
                  onClick={handleSyncToGoogleCalendar}
                  variant='outlined'
                  color='primary'
                  startIcon={
                     syncLoading || pendingSync ? <CircularProgress size={16} /> : <SyncIcon />
                  }
                  disabled={syncLoading || pendingSync}
                  fullWidth={isMobile}
                  sx={{
                     mr: isMobile ? 0 : 'auto',
                     mb: isMobile ? 1 : 0,
                     order: isMobile ? 2 : 0,
                  }}
               >
                  {syncLoading
                     ? 'Syncing...'
                     : pendingSync
                       ? 'Completing Auth...'
                       : 'Sync to My Google Calendar'}
               </Button>
            )}

            <Button
               onClick={handleViewDetails}
               variant='contained'
               color={getTypeColor() as any}
               startIcon={getTypeIcon()}
               fullWidth={isMobile}
               sx={{
                  order: isMobile ? 1 : 0,
                  py: isMobile ? 1.5 : 1,
               }}
            >
               View {data.type === 'trip' ? 'Trip' : data.type === 'event' ? 'Event' : 'Album'}{' '}
               Details
            </Button>

            <Button
               onClick={onClose}
               variant='outlined'
               fullWidth={isMobile}
               sx={{
                  order: isMobile ? 3 : 0,
                  mt: isMobile ? 1 : 0,
               }}
            >
               Close
            </Button>
         </DialogActions>

         {/* Success/Error Notifications */}
         <Snackbar
            open={syncSuccess}
            autoHideDuration={3000}
            onClose={() => setSyncSuccess(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert onClose={() => setSyncSuccess(false)} severity='success' sx={{ width: '100%' }}>
               Successfully synced to Google Calendar!
            </Alert>
         </Snackbar>

         <Snackbar
            open={!!syncError}
            autoHideDuration={6000}
            onClose={() => setSyncError(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert onClose={() => setSyncError(null)} severity='error' sx={{ width: '100%' }}>
               {syncError}
            </Alert>
         </Snackbar>
      </Dialog>
   );
};

export default TripEventModal;
